// The colleague-facing read path: GET /v1/employees/directory[/:id] (spec 18
// `feat_hris_directory`, E27).
//
// Why a second module next to `api.ts` and not more functions in it: `api.ts`
// reads the PERSONNEL record, which the server scopes by tier — an employee_self
// caller gets exactly one row (their own) from it. Every screen that needs a
// COLLEAGUE (the directory, the leave delegate, the kudos recipient, the meeting
// invitees) read that list and so offered a plain employee nobody but
// themselves. The directory endpoint returns a fixed, small projection for every
// active person in the caller's company grant; this file is its only client.
//
// The type below is the projection, field for field. A screen that needs a
// personnel field (sicil, start date, lifecycle) is a personnel screen and reads
// `api.ts` — there is deliberately no way to ask the directory for more.
//
// Server contract: services/platform-graph/src/routes/employee-directory.ts.

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import { type KeysetPage } from "@/api/keyset";

export interface DirectoryEntry {
  readonly employee_id: string;
  readonly legal_first_name: string;
  readonly legal_last_name: string;
  readonly primary_work_email: string;
  readonly job_title: string | null;
  readonly department_id: string | null;
  readonly department_name: string | null;
}

export interface DirectoryQuery {
  readonly cursor?: string;
  readonly q?: string;
  readonly departmentId?: string;
  readonly limit?: number;
}

/**
 * Query-key prefix for everything read through this module. It nests under
 * `["employees"]` on purpose: creating or editing a person already invalidates
 * that prefix, and a picker that kept a stale directory would keep offering
 * someone who has since left.
 */
export const DIRECTORY_KEY = ["employees", "directory"] as const;

// The server's own ceiling for `limit`. A picker is a bounded option set (a
// <select>), so it takes one full page rather than paging the user through it.
const PICKER_LIMIT = 500;

// ---- typed-cast call signatures for the consumed wire contracts ---------

type DirectoryGet = (
  path: "/v1/employees/directory",
  init: {
    params: {
      query: { cursor?: string; q?: string; department_id?: string; limit?: number };
    };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<DirectoryEntry>>>;

type DirectoryEntryGet = (
  path: "/v1/employees/directory/{id}",
  init: { params: { path: { id: string } } },
) => Promise<EnvelopedFetchResult<{ entry: DirectoryEntry }>>;

// ---- query functions -------------------------------------------------------

export async function fetchDirectoryPage(
  query: DirectoryQuery = {},
): Promise<KeysetPage<DirectoryEntry>> {
  const get = platformGraph.GET as unknown as DirectoryGet;
  return unwrapEnvelope(
    get("/v1/employees/directory", {
      params: {
        query: {
          ...(query.cursor !== undefined && { cursor: query.cursor }),
          ...(query.q !== undefined && query.q !== "" && { q: query.q }),
          ...(query.departmentId !== undefined &&
            query.departmentId !== "" && { department_id: query.departmentId }),
          ...(query.limit !== undefined && { limit: query.limit }),
        },
      },
    }),
  );
}

/** The option source for a person picker: one full page of the directory. */
export async function fetchDirectoryFirstPage(): Promise<KeysetPage<DirectoryEntry>> {
  return fetchDirectoryPage({ limit: PICKER_LIMIT });
}

export async function fetchDirectoryEntry(id: string): Promise<DirectoryEntry> {
  const get = platformGraph.GET as unknown as DirectoryEntryGet;
  const result = await unwrapEnvelope(
    get("/v1/employees/directory/{id}", { params: { path: { id } } }),
  );
  return result.entry;
}
